import { api } from './api'
import { getMe } from './me'
import { appHasPush, enquiryAlertsOn, reminderSupport, turnEnquiryAlertsOff, turnEnquiryAlertsOn } from './reminders'
import { el, toast } from './ui'
import { loadEmailLeads } from './waiting-ok'

// Quote alerts (Liam, 10 Oct 2026, pulled forward from the inbox plan: "Yes"). Two parts on the owner's side:
//   - a number on More, and on New jobs inside More: the customers waiting for an answer, by Needs you's own rule
//     (enquiries emailed in and waiting for the owner's OK, and quotes whose customer replied and nobody has marked
//     seen), so the number and Needs you never disagree;
//   - "Alerts on this phone" at the top of New jobs: a phone alert when an email or a reply arrives (never 9pm to
//     7am), as well as the email roundly-worker sends every time (worker/src/enquiry-alerts.ts).
// Only while quotes by email is on, and only for owners: loadEmailLeads answers nothing otherwise.

let waitingCount = 0
let sequence = 0

export function enquiryWaitingCount(): number {
  return waitingCount
}

function paint(n: number): void {
  waitingCount = n
  for (const id of ['more-badge', 'newjobs-more-badge']) {
    const badge = document.getElementById(id)
    if (!badge) continue
    badge.textContent = n > 0 ? String(n) : ''
    badge.setAttribute('aria-label', n > 0 ? `${n} waiting for your answer` : '')
  }
}

export async function refreshEnquiryBadge(): Promise<void> {
  const me = getMe()
  if (me?.role !== 'owner') return
  const mine = ++sequence
  try {
    const { waiting, replied } = await loadEmailLeads()
    if (mine === sequence) paint(waiting.length + replied.length)
  } catch {
    // No signal: the last number stays rather than reading as "nobody waiting".
  }
}

// Emails arrive with nobody touching the app, so the number is read again when the app comes back to the front and
// every two minutes while it is in front.
let watching = false
export function watchEnquiryBadge(): void {
  if (watching) return
  watching = true
  void refreshEnquiryBadge()
  window.addEventListener('dropby:data-changed', () => void refreshEnquiryBadge())
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void refreshEnquiryBadge() })
  window.setInterval(() => { if (document.visibilityState === 'visible') void refreshEnquiryBadge() }, 120_000)
}

// The row at the top of New jobs: what this phone does when an enquiry or a reply comes in.
export function enquiryAlertsRow(): HTMLElement | null {
  if (getMe()?.role !== 'owner') return null
  const row = el('section', { class: 'reminders-row enquiry-alerts-row', 'aria-label': 'Alerts for new enquiries', hidden: '' })
  const note = el('span', { class: 'reminders-note' })
  const words = el('div', { class: 'reminders-words' }, el('strong', {}, 'Alerts for new enquiries'), note)
  const button = el('button', { type: 'button', class: 'btn-secondary reminders-button' }) as HTMLButtonElement
  row.append(words, button)
  const support = reminderSupport()
  const always = 'You also get an email each time.'
  const show = (on: boolean): void => {
    note.textContent = on
      ? `On for this phone: an alert when someone emails your quotes address or replies to a quote. Not between 9pm and 7am. ${always}`
      : `Get an alert on this phone when someone emails your quotes address or replies to a quote. Not between 9pm and 7am. ${always}`
    button.textContent = on ? 'Turn off' : 'Turn on'
    button.setAttribute('aria-pressed', String(on))
  }
  // Shown only while quotes by email is on.
  void api<{ row: { quotes_by_email: boolean } }>('/api/business/quotes-email').then(async ({ row: quotes }) => {
    if (!quotes.quotes_by_email || !row.isConnected) return
    row.hidden = false
    // The Roundly iPhone app gives its own alerts from the build that asks Apple for them (10 Oct 2026); an older
    // build still says to update.
    if (support === 'app' && !(await appHasPush())) { note.textContent = `Update the Roundly app from TestFlight to get alerts on this iPhone. ${always}`; button.remove(); return }
    if (support === 'iphone-home-screen') { note.textContent = `On iPhone, add Roundly to your Home Screen first, then open it from there to turn alerts on. ${always}`; button.remove(); return }
    if (support === 'no') { note.textContent = `This browser can't show alerts. ${always}`; button.remove(); return }
    show(await enquiryAlertsOn())
  }).catch(() => {})
  button.addEventListener('click', () => {
    const on = button.getAttribute('aria-pressed') === 'true'
    button.disabled = true
    void (on ? turnEnquiryAlertsOff().then((ok) => (ok ? null : "Couldn't turn alerts off. Try again with signal.")) : turnEnquiryAlertsOn())
      .then(async (problem) => {
        if (problem) toast(problem)
        else toast(on ? 'Alerts off on this phone. The emails still come.' : 'Alerts on for this phone.')
        show(await enquiryAlertsOn())
      })
      .finally(() => { button.disabled = false })
  })
  return row
}
